import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { getProduct } from '../services/api';
import { companyInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ShoppingCart, ArrowLeft, Check, Shield, MessageCircle, Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the category from navigation state (if user came from a category)
  const fromCategory = location.state?.fromCategory;

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const data = await getProduct(id);
        setProduct(data);
        setError(null);
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Producto no encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Cargando producto...</p>
        </div>
      </div>
    );
  }

  // Define handleGoBack early so it can be used in error state
  const handleGoBack = () => {
    // Smart navigation: if user came from a category, go back to that category
    if (fromCategory) {
      navigate(`/catalog?category=${fromCategory}`);
    } else {
      // Otherwise, just go back in history or to catalog
      navigate('/catalog');
    }
  };

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{error || 'Producto no encontrado'}</h2>
          <Button onClick={handleGoBack}>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  const monthlyPayment = product.monthly_payment;
  const isAvailable = product.available !== false; // Default to true if not set

  const handleAddToCart = () => {
    if (isAvailable) {
      addToCart(product, quantity);
      toast.success(`${product.name} agregado al carrito`);
    }
  };

  const handleBuyNow = () => {
    if (!isAvailable) return;
    
    // Create WhatsApp message
    const message = `¡Hola! Me interesa comprar:\n\n*${product.name}*\nPrecio: RD$${product.price.toLocaleString()}\nFinanciamiento: Desde RD$${monthlyPayment.toLocaleString()}/mes\n\nQuiero más información para proceder con la compra.`;
    
    const whatsappUrl = `https://wa.me/${companyInfo.social.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
  };

  const handleShare = () => {
    const productUrl = window.location.href;
    
    // Try native share API first (works on mobile and modern browsers)
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: `Mira este producto: ${product.name} - RD$${product.price.toLocaleString()}`,
        url: productUrl
      }).catch(() => {
        // User cancelled or error
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(productUrl).then(() => {
        toast.success('¡Link copiado al portapapeles!');
      }).catch(() => {
        toast.error('No se pudo copiar el link');
      });
    }
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Back Button and Share */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={handleGoBack}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {fromCategory ? `Volver a ${fromCategory}` : 'Volver al catálogo'}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleShare}
            className="gap-2"
          >
            <Share2 className="h-4 w-4" />
            Compartir
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="relative aspect-square flex items-center justify-center rounded-lg bg-white p-6">
            <img
              src={product.image}
              alt={product.name}
              className="object-contain object-center w-full h-full"
            />
            {product.featured && (
              <Badge className="absolute top-4 right-4 bg-primary">
                {t('featured')}
              </Badge>
            )}
            
            {/* Unavailability Badge */}
            {!isAvailable && (
              <div className="absolute inset-0 bg-black/5 backdrop-blur-[2px] flex items-center justify-center rounded-lg">
                <div className="text-center space-y-3">
                  <Badge 
                    variant="destructive" 
                    className="bg-red-500/90 backdrop-blur-sm text-white px-6 py-3 text-base font-medium shadow-lg"
                  >
                    Producto fuera de servicio temporalmente
                  </Badge>
                  <p className="text-sm text-muted-foreground bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full">
                    Este producto no está disponible en este momento
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Brand */}
            <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {product.brand}
            </div>

            {/* Name */}
            <h1 className="text-4xl font-bold">{product.name}</h1>

            {/* Description */}
            <p className="text-lg text-muted-foreground leading-relaxed">
              {product.description}
            </p>

            <Separator />

            {/* Pricing */}
            <div className="space-y-3">
              <div className="text-4xl font-bold">
                RD${product.price.toLocaleString()}
              </div>
              
              <Card className="bg-muted/50 border-0">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('from')}</p>
                      <p className="text-2xl font-bold">
                        RD${monthlyPayment.toLocaleString()}<span className="text-base font-normal">{t('perMonth')}</span>
                      </p>
                    </div>
                    <MessageCircle className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('financingSubject')}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Separator />

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold mb-3">{t('features')}</h3>
              <ul className="space-y-2">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Warranty */}
            <div className="flex items-center space-x-2 text-sm">
              <Shield className="h-5 w-5 text-primary" />
              <span className="text-muted-foreground">
                {t('warranty')}: <span className="font-semibold text-foreground">{product.warranty}</span>
              </span>
            </div>

            <Separator />

            {/* Quantity Selector */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium">{t('quantity')}:</span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  -
                </Button>
                <span className="w-12 text-center font-semibold">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  +
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Add to Cart Button */}
              <Button
                size="lg"
                variant="outline"
                className="w-full text-lg py-6"
                onClick={handleAddToCart}
                disabled={!isAvailable}
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                {t('addToCart')}
              </Button>

              {/* Buy Now Button - Direct WhatsApp */}
              <Button
                size="lg"
                className="w-full text-lg py-6 bg-green-600 hover:bg-green-700"
                onClick={handleBuyNow}
                disabled={!isAvailable}
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Comprar ahora
              </Button>
              
              {!isAvailable && (
                <p className="text-sm text-red-500 text-center">
                  Este producto no está disponible temporalmente
                </p>
              )}
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-0 bg-muted/50">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Entrega</p>
                  <p className="font-semibold text-sm">Disponible</p>
                </CardContent>
              </Card>
              <Card className="border-0 bg-muted/50">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Instalación</p>
                  <p className="font-semibold text-sm">Costo adicional</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
